import axios from 'axios';
import errorReportingService from './errorReportingService';

// API Configuration
const API_BASE_URL = process.env.REACT_APP_API_URL || 'http://localhost:9003/api';
const API_TIMEOUT = parseInt(process.env.REACT_APP_API_TIMEOUT) || 30000;

// Create axios instance
const api = axios.create({
  baseURL: API_BASE_URL,
  timeout: API_TIMEOUT,
  headers: {
    'Content-Type': 'application/json'
  }
});

// Token management
const TOKEN_KEY = 'pestcrm_token';
const REFRESH_TOKEN_KEY = 'pestcrm_refresh_token';

export const tokenManager = {
  getToken: () => localStorage.getItem(TOKEN_KEY),
  getRefreshToken: () => localStorage.getItem(REFRESH_TOKEN_KEY),
  setTokens: (accessToken, refreshToken) => {
    localStorage.setItem(TOKEN_KEY, accessToken);
    if (refreshToken) {
      localStorage.setItem(REFRESH_TOKEN_KEY, refreshToken);
    }
  },
  clearTokens: () => {
    localStorage.removeItem(TOKEN_KEY);
    localStorage.removeItem(REFRESH_TOKEN_KEY);
  },
  hasValidToken: () => {
    const token = localStorage.getItem(TOKEN_KEY);
    if (!token) return false;
    
    try {
      // Basic JWT validation - check if not expired
      const payload = JSON.parse(atob(token.split('.')[1]));
      const currentTime = Date.now() / 1000;
      return payload.exp > currentTime;
    } catch (error) {
      return false;
    }
  }
};

// Request interceptor to add auth token and handle method override
api.interceptors.request.use(
  (config) => {
    const token = tokenManager.getToken();
    if (token) {
      config.headers.Authorization = `Bearer ${token}`;
    }

    // Apache mod_security blocks PUT/DELETE methods.
    // Convert them to POST with X-HTTP-Method-Override header.
    const method = config.method?.toUpperCase();
    if (method === 'PUT' || method === 'DELETE' || method === 'PATCH') {
      config.headers['X-HTTP-Method-Override'] = method;
      config.method = 'post';
      // DELETE requests may not have a body — ensure one exists for POST
      if (!config.data) {
        config.data = {};
      }
    }

    return config;
  },
  (error) => {
    return Promise.reject(error);
  }
);

// Response interceptor for token refresh and error handling
api.interceptors.response.use(
  (response) => {
    return response;
  },
  async (error) => {
    const originalRequest = error.config;

    // Report API errors for monitoring (before handling them)
    if (error.response?.status >= 500 || 
        error.response?.status === 404 || 
        error.response?.status === 408 || 
        error.response?.status === 429 ||
        !error.response) { // Network errors
      
      try {
        const apiError = new Error(
          error.response?.data?.message || 
          error.response?.data?.error || 
          error.message || 
          'API request failed'
        );
        apiError.name = error.response ? 'APIError' : 'NetworkError';

        await errorReportingService.reportApiError(apiError, {
          endpoint: originalRequest.url,
          method: originalRequest.method?.toUpperCase(),
          status: error.response?.status || null,
          requestBody: originalRequest.data ? JSON.parse(originalRequest.data) : null,
          response: error.response?.data || null
        });
      } catch (reportingError) {
        console.error('Failed to report API error:', reportingError);
      }
    }

    // Handle 401 errors (token expired)
    if (error.response?.status === 401 && !originalRequest._retry) {
      originalRequest._retry = true;

      try {
        const refreshToken = tokenManager.getRefreshToken();
        if (refreshToken) {
          const response = await axios.post(`${API_BASE_URL}/auth/refresh`, {
            refreshToken
          });

          const { accessToken } = response.data;
          tokenManager.setTokens(accessToken);

          // Retry original request with new token
          originalRequest.headers.Authorization = `Bearer ${accessToken}`;
          return api(originalRequest);
        }
      } catch (refreshError) {
        // Refresh failed, clear tokens and redirect to login
        tokenManager.clearTokens();
        window.location.href = '/login';
        return Promise.reject(refreshError);
      }
    }

    return Promise.reject(error);
  }
);

// API helper functions
export const apiHelpers = {
  // Handle API errors consistently
  handleError: (error) => {
    if (error.response) {
      // Server responded with error status
      const message = error.response.data?.error || error.response.data?.message || 'Server error occurred';
      return new Error(message);
    } else if (error.request) {
      // Request made but no response received
      return new Error('Network error. Please check your connection.');
    } else {
      // Something else happened
      return new Error(error.message || 'An unexpected error occurred');
    }
  },

  // Retry logic for failed requests
  retry: async (fn, retries = 3, delay = 1000) => {
    try {
      return await fn();
    } catch (error) {
      if (retries > 0 && error.response?.status >= 500) {
        await new Promise(resolve => setTimeout(resolve, delay));
        return apiHelpers.retry(fn, retries - 1, delay * 2);
      }
      throw error;
    }
  },

  // Format API responses consistently
  formatResponse: (response) => {
    return {
      data: response.data,
      status: response.status,
      headers: response.headers
    };
  },

  // Test error reporting (development only)
  testErrorReporting: async () => {
    if (process.env.NODE_ENV !== 'development') {
      console.log('Test error reporting only available in development');
      return;
    }

    console.log('Testing API error reporting through axios interceptor...');

    try {
      // This should trigger a 404 error and send an email
      await api.get('/non-existent-endpoint');
    } catch (error) {
      console.log('Expected 404 error caught:', error.message);
    }

    try {
      // This should trigger a 500 error if we can create one
      await api.post('/error-reporting/test-500-error', { test: 'data' });
    } catch (error) {
      console.log('Expected server error caught:', error.message);
    }

    console.log('API error reporting tests completed through interceptor');
  }
};

// Generic CRUD operations
export const crudOperations = {
  // GET request
  get: async (endpoint, params = {}) => {
    try {
      const response = await api.get(endpoint, { params });
      return apiHelpers.formatResponse(response);
    } catch (error) {
      throw apiHelpers.handleError(error);
    }
  },

  // POST request
  post: async (endpoint, data = {}) => {
    try {
      const response = await api.post(endpoint, data);
      return apiHelpers.formatResponse(response);
    } catch (error) {
      throw apiHelpers.handleError(error);
    }
  },

  // PUT request
  put: async (endpoint, data = {}) => {
    try {
      const response = await api.put(endpoint, data);
      return apiHelpers.formatResponse(response);
    } catch (error) {
      throw apiHelpers.handleError(error);
    }
  },

  // DELETE request
  delete: async (endpoint) => {
    try {
      const response = await api.delete(endpoint);
      return apiHelpers.formatResponse(response);
    } catch (error) {
      throw apiHelpers.handleError(error);
    }
  },

  // File upload
  upload: async (endpoint, formData) => {
    try {
      const response = await api.post(endpoint, formData, {
        headers: {
          'Content-Type': 'multipart/form-data'
        }
      });
      return apiHelpers.formatResponse(response);
    } catch (error) {
      throw apiHelpers.handleError(error);
    }
  }
};

export default api;